import type { Observable } from 'rxjs';
import { from } from 'rxjs';
import type { CategoryDetailsForm, CategoryDetailsFormValues } from './category-details-form';
import type { WritableSignal } from '@angular/core';
import { TypeArrayConstants } from '../../constants/type-arrays.constants';

export interface CategoryFormService<T extends CategoryDetailsFormValues> {
  readonly i18nPrefix: WritableSignal<string>;

  createForm(): CategoryDetailsForm;

  get(id: string): Observable<T>;

  create(data: T): Observable<void>;

  update(id: string, data: T): Observable<void>;
}

export abstract class AbstractCategoryFormService<
  T extends CategoryDetailsFormValues,
> implements CategoryFormService<T> {
  abstract readonly i18nPrefix: WritableSignal<string>;
  abstract createForm(): CategoryDetailsForm;

  abstract readonly findCategoryAsync: (_: {
    path: { id: string };
  }) => Promise<{ data: T & { id: string } }>;
  abstract readonly createCategoryAsync: (_: { body: T }) => Promise<unknown>;
  abstract readonly updateCategoryAsync: (_: { path: { id: string }; body: T }) => Promise<unknown>;

  get(id: string): Observable<T> {
    // @ts-expect-error Because T could be instantiated with a different subtype of constraint
    return from(
      this.findCategoryAsync({
        path: { id },
      }).then(resp => {
        const { id, ...rest } = resp.data;
        return {
          ...rest,
          translations: [
            ...rest.translations,
            ...TypeArrayConstants.LANGUAGE_CODES.filter(
              l => !rest.translations.some(t => t.language === l),
            ).map(lang => ({
              language: lang,
              name: '',
            })),
          ],
        };
      }),
    );
  }

  create(data: T): Observable<void> {
    return from(
      this.createCategoryAsync({ body: data }).then(() => {
        // type cast to void
      }),
    );
  }

  update(id: string, body: T): Observable<void> {
    return from(
      this.updateCategoryAsync({
        path: { id },
        body,
      }).then(() => {
        // type cast to void
      }),
    );
  }
}
